import React, { use, useEffect, useState } from 'react';
import { Alert, Offcanvas, Button, Form, Card, Row, Col, Image, Spinner } from 'react-bootstrap';
import { ExclamationCircleFill, CheckCircleFill, ExclamationTriangleFill } from 'react-bootstrap-icons';
import { FormSelect } from 'components/elements/form-select/FormSelect';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

// import sub components
import DynamicForm from './DynamicForm';

// Import authentication components
import useAuthHeader from 'react-auth-kit/hooks/useAuthHeader';
import useAuthUser from 'react-auth-kit/hooks/useAuthUser';

function OffcanvasUpdateFlowConfigOption({ show, onHide, flow, selectedRow, formValues, onChange, onSubmit, isLoadingButton }) {
    const { t } = useTranslation();

    // API-Call Options for dynamic Form
    const authUser = useAuthUser(); // Assuming this gets the auth user data
    const authHeader = useAuthHeader();

    //
    // State management
    //

    // State for selected form values
    const [selectedFormValues, setSelectedFormValues] = useState({});
    const [selectedSection, setSelectedSection] = useState(null); // Default section based on selectedRow.id
    const [currentSectionConfig, setCurrentSectionConfig] = useState(null);

    // Select the selected section based on selectedRow.id
    useEffect(() => {
        if (selectedRow) {
            setSelectedSection(selectedRow);
        }
    }, [selectedRow]);

    //
    // Load configuration options dynamically
    //

    // Load the configuration for the selected section dynamically
    useEffect(() => {
        const loadConfigurationOptions = async () => {
            try {
                // Ermittle die Konfiguration basierend auf dem Flow config_key
                const flowConfigKey = flow.config_key?.split('.').slice(0, 2).join('.') || 'undefined';
                const configModule = await import(`data/dashboard/marketplace/configs/${flowConfigKey}.js`);
                setCurrentSectionConfig(configModule.default.sections[selectedSection] || null);
            } catch (error) {
                console.error('Error loading flow configuration:', error);
                setCurrentSectionConfig(null);
            }
        };

        loadConfigurationOptions();
    }, [flow, selectedSection]);

    //
    // Load form values for the selected section
    //

    useEffect(() => {
        if (formValues && selectedRow) {
            // Find the key where id matches selectedRow
            const key = Object.keys(formValues).find(
                k => formValues[k].id === selectedRow
            );
            if (key) {
                const config = formValues[key]?.configuration || {};
                setSelectedFormValues(config);
            } else {
                console.warn('No matching configuration found for selectedRow:', selectedRow);
                setSelectedFormValues({}); // Set to empty object if no config is found
            }

        } else {
            console.warn('formValues or selectedRow is null:', formValues, selectedRow);
            setSelectedFormValues({}); // Set to empty object if formValues or selectedRow is null
        }
    }, [formValues, selectedRow]);

    return (
        <Offcanvas show={show} onHide={onHide} placement="end">
            <Offcanvas.Header closeButton>
                <Offcanvas.Title>
                    <h2>
                        {currentSectionConfig ? currentSectionConfig.title : ''}
                    </h2>
                </Offcanvas.Title>
            </Offcanvas.Header>
            <Offcanvas.Body>
                {!currentSectionConfig ? (
                    <Col className="text-center">
                        <Alert variant="warning" className="bg-warning rounded">
                            <ExclamationTriangleFill className="me-2" />
                            {t('No form data available for this configuration section.')}{' '} {t('Please contact helpdesk and provide the following information:')}
                            <br />
                            <br />
                            <strong>{t('Configuration ID:')}</strong> {flow.id} <br />
                            <strong>{t('Configuration Key:')}</strong> {flow.config_key} <br />
                            <strong>{t('Configuration Step ID:')}</strong> {selectedRow ? selectedRow : ''}
                            <br />
                            <strong>{t('Configuration Step Title:')}</strong> {currentSectionConfig ? currentSectionConfig.title : ''}
                            <br />
                        </Alert>
                    </Col>
                ) : (
                    // DynamicForm //
                    <Form onSubmit={onSubmit}>
                        <DynamicForm
                            flow={flow}
                            formData={currentSectionConfig?.form}
                            formValues={selectedFormValues}
                            onChange={onChange}
                            authHeader={authHeader}
                            authUser={authUser}
                        />
                        <div className="d-flex justify-content-end mt-4">
                            <Button variant="secondary" onClick={onHide}>
                                {t('Cancel')}
                            </Button>
                            <Button className="ms-2 d-end" variant="primary" type="submit" disabled={isLoadingButton}>
                                {isLoadingButton ? (
                                    <>
                                        <Spinner animation="border" size="sm" className="me-2" />
                                        {t('Save configuration')}
                                    </>
                                ) : (
                                        t('Save configuration')
                                )}
                            </Button>
                        </div>
                    </Form>
                )}
            </Offcanvas.Body>
        </Offcanvas>
    );
}

export default OffcanvasUpdateFlowConfigOption;